import { useEffect, useState, useCallback, useRef } from 'react';
import withAbortAwareRetry from 'src/js/modules/utils/with-abort-aware-retry';
import utils from 'src/js/modules/utils';
import filterOutDuplicatedItems from '../helpers/filter-out-duplicated-items';
import useGallerySelection from './use-gallery-selection';
import getSelectedItems from '../helpers/get-selected-items';
import applySelectionToItems from '../helpers/apply-selection-to-items';

const {
  hooks: { useAbortController },
} = utils;

function useGallery({
  apiAdapter,
  searchQuery: sharedSearchQuery = '',
  multiple = false,
  isActive = true,
  updateSelectedItems = Function.prototype,
  updateSearchQuery,
  onSearchEnd = Function.prototype,
  onLoadError = Function.prototype,
  getInitialSearchQuery,
}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLimit, setIsPageLimit] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);

  const photoPaginator = useRef();
  const isInitialMount = useRef(true);
  const previousSearchQuery = useRef(sharedSearchQuery);
  const { getAbortController, getCurrentAbortController, isAbortError } = useAbortController();
  const { handleItemClick } = useGallerySelection({
    items,
    multiple,
  });

  const runInitialLoad = useCallback(async () => {
    const abortController = getAbortController();

    setIsLoading(true);
    setIsPageLimit(false);
    setIsNotFound(false);

    try {
      let queryToUse = '';

      if (typeof sharedSearchQuery === 'string') {
        queryToUse = sharedSearchQuery;
      } else if (getInitialSearchQuery) {
        queryToUse = await getInitialSearchQuery();
      }

      if (abortController.signal.aborted) {
        return;
      }

      photoPaginator.current = await apiAdapter.getInitialPhotos({
        query: queryToUse,
        abortController,
      });

      if (abortController.signal.aborted) {
        return;
      }

      previousSearchQuery.current = queryToUse;
      updateSearchQuery(queryToUse);

      const getPhotoPaginatorWithRetry = withAbortAwareRetry(
        photoPaginator.current.next.bind(photoPaginator.current),
        {
          abortController,
          abortErrorChecker: isAbortError,
        }
      );

      const { data } = await getPhotoPaginatorWithRetry();

      if (abortController.signal.aborted) {
        return;
      }

      const computedData = apiAdapter.getComputedData(data);

      setIsLoading(false);
      setItems(computedData);
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      console.error(error);
      setIsLoading(false);
      onLoadError(error);
    }
  }, [
    apiAdapter,
    getInitialSearchQuery,
    onLoadError,
    sharedSearchQuery,
    updateSearchQuery,
    getAbortController,
    isAbortError,
  ]);

  const runSearch = useCallback(
    async ({ query, searchTerm }) => {
      const abortController = getAbortController();
      const trimmedQuery = (query || '').trim();

      setIsSearching(true);
      setIsLoading(true);
      setItems([]);
      setIsPageLimit(false);
      setIsNotFound(false);

      try {
        photoPaginator.current = await (trimmedQuery
          ? apiAdapter.searchPhotos({ query: trimmedQuery, abortController })
          : apiAdapter.getPhotos({ abortController }));

        if (abortController.signal.aborted) {
          return;
        }

        const getPhotoPaginatorWithRetry = withAbortAwareRetry(
          photoPaginator.current.next.bind(photoPaginator.current),
          {
            abortController,
            abortErrorChecker: isAbortError,
          }
        );

        const { data } = await getPhotoPaginatorWithRetry();

        if (abortController.signal.aborted) {
          return;
        }

        const computedData = apiAdapter.getComputedData(data);

        if (!computedData.length) {
          setIsNotFound(true);
        }

        setItems(computedData);
        setIsSearching(false);
        setIsLoading(false);

        onSearchEnd({
          searchTerm,
          searchResults: computedData.length,
        });
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        console.error(error);
        setIsSearching(false);
        setIsLoading(false);
        onLoadError(error);
      }
    },
    [apiAdapter, onSearchEnd, onLoadError, getAbortController, isAbortError]
  );

  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (!isInitialMount.current) {
      return;
    }

    isInitialMount.current = false;

    runInitialLoad();
  }, [isActive, runInitialLoad]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (previousSearchQuery.current === sharedSearchQuery) {
      return;
    }

    previousSearchQuery.current = sharedSearchQuery;

    runSearch({
      query: sharedSearchQuery,
      searchTerm: sharedSearchQuery,
    });
  }, [isActive, sharedSearchQuery, runSearch]);

  useEffect(() => {
    if (isActive) {
      return;
    }

    setItems((prevItems) =>
      applySelectionToItems(
        prevItems,
        prevItems.map((item) => item.id),
        false
      )
    );
  }, [isActive]);

  const handleSearch = useCallback(
    async (value = '') => {
      const trimmedValue = value.trim();

      previousSearchQuery.current = trimmedValue;
      updateSearchQuery(trimmedValue);

      await runSearch({
        query: trimmedValue,
        searchTerm: trimmedValue,
      });
    },
    [updateSearchQuery, runSearch]
  );

  const handleSelect = useCallback(
    (id, e) => {
      const updatedItems = handleItemClick(id, e);
      setItems(updatedItems);
      updateSelectedItems(getSelectedItems(updatedItems));
    },
    [handleItemClick, updateSelectedItems]
  );

  const handleLoadMoreItems = useCallback(async () => {
    if (isLoading || isPageLimit || isSearching || items.length === 0) {
      return;
    }

    const abortController = getCurrentAbortController();

    if (!photoPaginator.current || !abortController || abortController.signal.aborted) {
      return;
    }

    setIsLoading(true);

    const getNextPageWithRetry = withAbortAwareRetry(
      photoPaginator.current.next.bind(photoPaginator.current),
      {
        abortController,
        abortErrorChecker: isAbortError,
      }
    );

    try {
      const { data } = await getNextPageWithRetry();

      if (abortController.signal.aborted) {
        setIsLoading(false);
        return;
      }

      const computedData = apiAdapter.getComputedData(data);

      setIsLoading(false);

      if (!computedData.length) {
        setIsPageLimit(true);
        return;
      }

      setItems((prev) => filterOutDuplicatedItems([...prev, ...computedData]));
    } catch (error) {
      if (isAbortError(error)) {
        setIsLoading(false);
        return;
      }

      console.error(error);
      setIsLoading(false);
      setIsPageLimit(true);
      onLoadError(error);
    }
  }, [
    items,
    isLoading,
    isPageLimit,
    isSearching,
    apiAdapter,
    onLoadError,
    getCurrentAbortController,
    isAbortError,
  ]);

  return {
    items,
    isLoading,
    isSearching,
    isNotFound,
    handleSearch,
    handleSelect,
    handleLoadMoreItems,
  };
}

export default useGallery;
